"use client";

/**
 * Client body for the /pricing page. Lives apart from `page.tsx` (which stays a
 * server component for `metadata` and the server-rendered JSON-LD) so it can
 * call `useT()` and follow the visitor's locale. All copy comes from the
 * `pricingPage` namespace; structure (icons, hrefs, section ids) stays here.
 */

import {
  ShieldCheck,
  Inbox,
  GitBranch,
  Rocket,
  Lock,
  ClipboardCheck,
  Calculator,
  CircleSlash,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  Section,
  SectionHeader,
  Faq,
  RelatedLinks,
  CtaBanner,
  Eyebrow,
  PrimaryCTA,
  SecondaryCTA,
} from "@/components/marketing/marketing-kit";
import { PricingTiers } from "@/components/marketing/pricing-tiers";
import { PricingComparison } from "@/components/marketing/pricing-comparison";
import { PricingStructuredData } from "@/components/seo/pricing-structured-data";
import { PRICING_FAQ } from "@/lib/marketing-pricing";
import { useT } from "@/i18n";
import type { T } from "@/i18n/translate";

/* -------------------------------------------------------------------------- */
/*  HERO                                                                       */
/* -------------------------------------------------------------------------- */

function PricingHero({ t }: { t: T }) {
  return (
    <section className="relative overflow-hidden border-b border-line-subtle">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-40 h-[560px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.18),transparent_60%)]"
      />
      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-5 pt-16 pb-14 text-center sm:px-6 md:pt-24 md:pb-16">
        <Eyebrow>{t("pricingPage.hero.eyebrow")}</Eyebrow>
        <h1 className="mt-6 text-balance text-[34px] font-semibold leading-[1.08] tracking-tight text-fg-bright sm:text-5xl md:text-6xl">
          {t("pricingPage.hero.title")}{" "}
          <span className="bg-brand-grad bg-clip-text text-transparent">
            {t("pricingPage.hero.highlight")}
          </span>
        </h1>
        <p className="mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-fg-dim">
          {t("pricingPage.hero.subtitle")}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <PrimaryCTA href="/signup">{t("pricingPage.hero.primaryCta")}</PrimaryCTA>
          <SecondaryCTA href="/demo">{t("pricingPage.hero.secondaryCta")}</SecondaryCTA>
        </div>
        <p className="mt-8 max-w-2xl text-pretty text-[13px] leading-relaxed text-fg-muted">
          {t("pricingPage.hero.footnote")}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  WORKED EXAMPLE — AI actions in euros                                       */
/* -------------------------------------------------------------------------- */

function PricingExample({ t }: { t: T }) {
  return (
    <div className="mt-16">
      <SectionHeader
        eyebrow={t("pricingPage.example.eyebrow")}
        title={t("pricingPage.example.title")}
        description={t("pricingPage.example.description")}
      />
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2">
        {(["small", "medium"] as const).map((k) => (
          <div key={k} className="rounded-2xl border border-line-subtle bg-bg-raised p-6">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
              <Calculator size={18} />
            </span>
            <h3 className="mt-5 text-[16px] font-semibold text-fg-bright">
              {t(`pricingPage.example.${k}.title`)}
            </h3>
            <p className="mt-2 text-[13px] text-fg-dim leading-relaxed">
              {t(`pricingPage.example.${k}.body`)}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-line-subtle bg-bg-raised/60 p-6">
        <CircleSlash size={18} className="mt-0.5 flex-shrink-0 text-brand-300" />
        <div>
          <h3 className="text-[15px] font-semibold text-fg-bright">
            {t("pricingPage.example.limit.title")}
          </h3>
          <p className="mt-1 text-[13px] text-fg-dim leading-relaxed">
            {t("pricingPage.example.limit.body")}
          </p>
        </div>
      </div>
      <p className="mt-6 text-center text-[12px] text-fg-muted">
        {t("pricingPage.example.footnote")}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  TRIAL / PILOT                                                              */
/* -------------------------------------------------------------------------- */

function TrialPilot({ t }: { t: T }) {
  return (
    <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2">
      <div className="rounded-2xl border border-line-subtle bg-bg-raised p-6">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
          <Inbox size={18} />
        </span>
        <h3 className="mt-5 text-[16px] font-semibold text-fg-bright">
          {t("pricingPage.trial.starterTeam.title")}
        </h3>
        <ul className="mt-3 space-y-2 text-[13px] text-fg-dim leading-relaxed">
          <li>{t("pricingPage.trial.starterTeam.b1")}</li>
          <li>{t("pricingPage.trial.starterTeam.b2")}</li>
          <li>{t("pricingPage.trial.starterTeam.b3")}</li>
          <li>{t("pricingPage.trial.starterTeam.b4")}</li>
        </ul>
      </div>
      <div className="rounded-2xl border border-brand-500/30 bg-bg-raised p-6 shadow-[0_30px_80px_-30px_rgba(244,63,142,0.4)]">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-grad text-white shadow-glow">
          <Rocket size={18} />
        </span>
        <h3 className="mt-5 text-[16px] font-semibold text-fg-bright">
          {t("pricingPage.trial.proEnterprise.title")}
        </h3>
        <ul className="mt-3 space-y-2 text-[13px] text-fg-dim leading-relaxed">
          <li>{t("pricingPage.trial.proEnterprise.b1")}</li>
          <li>{t("pricingPage.trial.proEnterprise.b2")}</li>
          <li>{t("pricingPage.trial.proEnterprise.b3")}</li>
          <li>{t("pricingPage.trial.proEnterprise.b4")}</li>
        </ul>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PAGE BODY                                                                  */
/* -------------------------------------------------------------------------- */

export function PricingContent() {
  const t = useT();

  return (
    <MarketingShell>
      <PricingStructuredData />
      <PricingHero t={t} />

      {/* Tier cards + billing toggle */}
      <Section id="plans">
        <PricingTiers />
        <PricingExample t={t} />
      </Section>

      {/* Positioning — make €99 read as an entry point, not the product */}
      <Section muted>
        <SectionHeader
          eyebrow={t("pricingPage.positioning.eyebrow")}
          title={t("pricingPage.positioning.title")}
          description={t("pricingPage.positioning.description")}
        />
        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: <Inbox size={18} />,
              name: t("pricingPage.positioning.cards.starter.name"),
              body: t("pricingPage.positioning.cards.starter.body"),
            },
            {
              icon: <ShieldCheck size={18} />,
              name: t("pricingPage.positioning.cards.team.name"),
              body: t("pricingPage.positioning.cards.team.body"),
            },
            {
              icon: <GitBranch size={18} />,
              name: t("pricingPage.positioning.cards.pro.name"),
              body: t("pricingPage.positioning.cards.pro.body"),
            },
            {
              icon: <Lock size={18} />,
              name: t("pricingPage.positioning.cards.enterprise.name"),
              body: t("pricingPage.positioning.cards.enterprise.body"),
            },
          ].map((c) => (
            <div
              key={c.name}
              className="h-full rounded-2xl border border-line-subtle bg-bg-raised p-6"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
                {c.icon}
              </span>
              <div className="mt-5 text-[15px] font-semibold text-fg-bright">{c.name}</div>
              <p className="mt-2 text-[13px] text-fg-dim leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Comparison matrix */}
      <Section id="compare">
        <SectionHeader
          eyebrow={t("pricingPage.compare.eyebrow")}
          title={t("pricingPage.compare.title")}
          description={t("pricingPage.compare.description")}
        />
        <div className="mt-12">
          <PricingComparison />
        </div>
      </Section>

      {/* Trial / pilot */}
      <Section muted id="trial">
        <SectionHeader
          eyebrow={t("pricingPage.trialSection.eyebrow")}
          title={t("pricingPage.trialSection.title")}
          description={t("pricingPage.trialSection.description")}
        />
        <TrialPilot t={t} />
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <SectionHeader eyebrow={t("pricingPage.faqSection.eyebrow")} title={t("pricingPage.faqSection.title")} />
        <Faq items={PRICING_FAQ.map((f) => ({ q: t(f.q), a: t(f.a) }))} />
        <div className="mx-auto mt-8 flex max-w-3xl items-start gap-3 rounded-2xl border border-line-subtle bg-bg-raised/60 p-5">
          <ClipboardCheck size={18} className="mt-0.5 flex-shrink-0 text-brand-300" />
          <p className="text-[13px] text-fg-dim leading-relaxed">
            {t("pricingPage.note")}
          </p>
        </div>
      </Section>

      {/* Related links */}
      <Section muted>
        <RelatedLinks
          title={t("pricingPage.related.title")}
          links={[
            {
              href: "/use-cases/slack-jira-ticket-questions",
              label: t("pricingPage.related.ticketQuestions.label"),
              description: t("pricingPage.related.ticketQuestions.description"),
            },
            {
              href: "/use-cases/freshdesk-jira-approved-replies",
              label: t("pricingPage.related.approvedReplies.label"),
              description: t("pricingPage.related.approvedReplies.description"),
            },
            {
              href: "/use-cases/support-to-engineering-remediation",
              label: t("pricingPage.related.remediation.label"),
              description: t("pricingPage.related.remediation.description"),
            },
            {
              href: "/use-cases/ai-agent-approval-ledger",
              label: t("pricingPage.related.approvalLedger.label"),
              description: t("pricingPage.related.approvalLedger.description"),
            },
            {
              href: "/use-cases/ai-deployment-governance",
              label: t("pricingPage.related.deployment.label"),
              description: t("pricingPage.related.deployment.description"),
            },
            {
              href: "/integrations/jira",
              label: t("pricingPage.related.jira.label"),
              description: t("pricingPage.related.jira.description"),
            },
            {
              href: "/integrations/freshdesk",
              label: t("pricingPage.related.freshdesk.label"),
              description: t("pricingPage.related.freshdesk.description"),
            },
            {
              href: "/integrations/slack",
              label: t("pricingPage.related.slack.label"),
              description: t("pricingPage.related.slack.description"),
            },
            {
              href: "/docs/en/admin/security",
              label: t("pricingPage.related.security.label"),
              description: t("pricingPage.related.security.description"),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t("pricingPage.cta.title")}
        description={t("pricingPage.cta.description")}
        primary={{ href: "/signup", label: t("pricingPage.cta.primary") }}
        secondary={{ href: "/demo", label: t("pricingPage.cta.secondary") }}
      />
    </MarketingShell>
  );
}
